import React, { ChangeEvent, KeyboardEvent, ReactNode, memo, useCallback } from "react";
import "./textarea.scss";
import { FormInputElement } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";
import { InfoIcon } from "@components/icons";
import { CustomTooltip } from "@components/tool-tip";

export interface TextareaProps extends FormInputElement {
  onChange?: (value: string) => void;
  value?: string;
  containerClassName?: string;
  fieldIcon?: ReactNode;
  errorMessage?: string;
  maxLength?: number;
  labelClassName?: string;
  infoText?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyPress?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  wrapperClass?: string;
  rows?: number;
  cols?: number;
  disabled?: boolean;
  flexDirection?: string;
  className?: string;
  style?: React.CSSProperties;
  tooltipMessage?: string;
  id?: string;
  placeholder?: string;
  height?: string | number;
  ref?: React.Ref<HTMLTextAreaElement>;
}

const TextareaComponent = ({
  onChange,
  placeholder = "Type Something...",
  label,
  containerClassName,
  fieldIcon,
  children,
  errorMessage,
  required,
  labelClassName,
  infoText,
  wrapperClass,
  value,
  flexDirection,
  className,
  style,
  tooltipMessage,
  id,
  rows = 3,
  ref,
  ...restProps
}: TextareaProps) => {
  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const inputValue = e.target.value;
      onChange?.(inputValue);
    },
    [onChange],
  );

  return (
    <div className={`${containerClassName ?? ""} w-100`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
        htmlFor={id}
      />

      <div
        className={`textarea-wrapper ${!fieldIcon ? "flex" : ""} ${errorMessage ? "error" : ""} ${
          flexDirection === "column" ? "flex-col" : ""
        } ${wrapperClass}`}
      >
        {fieldIcon && <span className="field-icon">{fieldIcon}</span>}

        <textarea
          ref={ref}
          id={id}
          rows={rows}
          value={value ?? ""}
          onChange={handleTextChange}
          placeholder={placeholder}
          className={`textarea${className ? ` ${className}` : ""}`}
          style={style}
          {...restProps}
        />

        {tooltipMessage && (
          <CustomTooltip
            content={tooltipMessage}
            maxWidth={240}
            className="text-wrap"
            wrapperClass="align-center"
          >
            <InfoIcon color="#999999" size="10" />
          </CustomTooltip>
        )}

        {children}
      </div>

      <FormError errorMessage={errorMessage} />
    </div>
  );
};

TextareaComponent.displayName = "TextareaComponent";

export const Textarea = memo(TextareaComponent);
